import { Button } from "@/components/button"
import { AnimatedListItem } from "@/components/ui/animated-list"
import { BlurFade } from "@/components/ui/blur-fade"
import { cn } from "@/lib/utils"

import { getToneClasses } from "./tone-styles"

export type AppToastTone = "success" | "error"

export type AppToast = {
  id: number
  tone: AppToastTone
  text: string
  actionLabel?: string
  onAction?: () => void
}

export function ToastStack({
  toasts,
  dismissToast,
  successTitle = "Saved",
  errorTitle = "Sync error",
  className,
}: {
  toasts: AppToast[]
  dismissToast: (toastId: number) => void
  successTitle?: string
  errorTitle?: string
  className?: string
}) {
  if (toasts.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-4 bottom-4 z-50 grid gap-3 md:left-auto md:w-104",
        className,
      )}
      aria-live="polite"
      aria-relevant="additions text"
    >
      {toasts.map((toast, index) => (
        <BlurFade
          key={toast.id}
          delay={index * 0.02}
          duration={0.22}
          offset={6}
          blur="4px"
        >
          <AnimatedListItem>
            <div
              role={toast.tone === "success" ? "status" : "alert"}
              aria-live={toast.tone === "success" ? "polite" : "assertive"}
              aria-atomic="true"
              className={cn(
                "pointer-events-auto rounded-2xl border px-4 py-3 text-sm shadow-xl backdrop-blur",
                getToneClasses(toast.tone === "success" ? "success" : "danger"),
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="grid gap-1">
                  <div className="text-kicker font-semibold opacity-70">
                    {toast.tone === "success" ? successTitle : errorTitle}
                  </div>
                  <div>{toast.text}</div>
                </div>
                <div className="flex items-center gap-2">
                  {toast.actionLabel && toast.onAction ? (
                    <Button
                      unstyled
                      type="button"
                      className="rounded-full border border-current/20 px-2.5 py-1 text-xs font-semibold"
                      onClick={() => {
                        dismissToast(toast.id)
                        toast.onAction?.()
                      }}
                    >
                      {toast.actionLabel}
                    </Button>
                  ) : null}
                  <Button
                    unstyled
                    type="button"
                    className="text-xs opacity-70 transition-opacity hover:opacity-100"
                    onClick={() => dismissToast(toast.id)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </AnimatedListItem>
        </BlurFade>
      ))}
    </div>
  )
}
